import { getFirestoreSdk, type FirestoreSdk } from '@/lib/firestoreSdk';
import type { QueryConstraint } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';

export interface FirestoreCollectionResult<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  fromCache: boolean;
  hasPendingWrites: boolean;
}

/**
 * Builds the query constraints once the Firestore SDK has loaded. Constraint
 * functions (`where`, `orderBy`, `limit`, ...) live on the lazily-loaded SDK
 * namespace rather than as static top-level imports — see @/lib/firestoreSdk
 * for why a static `import ... from 'firebase/firestore'` anywhere in the
 * eager-reachable graph defeats the lazy-load chunk boundary.
 */
export type FirestoreConstraintsBuilder = (sdk: FirestoreSdk) => QueryConstraint[];

/**
 * Subscribes to a Firestore collection (or query) and returns the list plus
 * offline metadata.
 *
 * IMPORTANT: `buildConstraints` is used directly in the effect's dependency
 * list. Callers MUST memoize it (`useCallback`) — otherwise the subscription
 * tears down and reconnects on every render. Recommended pattern:
 *
 *   const buildConstraints = useCallback<FirestoreConstraintsBuilder>(
 *     (sdk) => [sdk.where('current_owner_uid', '==', uid), sdk.orderBy('updatedAt', 'desc')],
 *     [uid],
 *   );
 *   const { data } = useFirestoreCollection<Vehicle>('vehicles', buildConstraints);
 */
export function useFirestoreCollection<T>(
  path: string | null,
  buildConstraints?: FirestoreConstraintsBuilder,
): FirestoreCollectionResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(path !== null);
  const [error, setError] = useState<Error | null>(null);
  const [fromCache, setFromCache] = useState<boolean>(false);
  const [hasPendingWrites, setHasPendingWrites] = useState<boolean>(false);
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!path) {
      prevPathRef.current = null;
      setData([]);
      setLoading(false);
      setError(null);
      setFromCache(false);
      setHasPendingWrites(false);
      return;
    }

    // Reset stale data from the previous path so vehicle A's data never
    // flashes while the subscription for vehicle B is establishing. Only on a
    // REAL path change — when the effect re-runs because a caller passed a
    // non-memoized builder (identity churn), blanking to [] would flash
    // empty on every render instead of merely re-subscribing.
    if (prevPathRef.current !== path) {
      setData([]);
      setFromCache(false);
      setHasPendingWrites(false);
      setLoading(true);
      setError(null);
    }
    prevPathRef.current = path;

    let cancelled = false;
    let unsub: (() => void) | undefined;

    getFirestoreSdk()
      .then(({ sdk, db }) => {
        if (cancelled) return;
        const ref = sdk.collection(db, path);
        const constraints = buildConstraints ? buildConstraints(sdk) : [];
        const q = constraints.length > 0 ? sdk.query(ref, ...constraints) : ref;

        unsub = sdk.onSnapshot(
          q,
          { includeMetadataChanges: true },
          (snap) => {
            if (cancelled) return;
            const rows: T[] = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T);
            setData(rows);
            setFromCache(snap.metadata.fromCache);
            setHasPendingWrites(snap.metadata.hasPendingWrites);
            setLoading(false);
          },
          (err) => {
            if (cancelled) return;
            setError(err);
            setLoading(false);
          },
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to load Firestore'));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [path, buildConstraints]);

  return { data, loading, error, fromCache, hasPendingWrites };
}
